import type { HttpPatch } from '@/shared/infra/http'
import { HttpError } from '@/shared/infra/http'
import type { MeasurementSystem, UserProfile } from '@/shared/domain'
import {
  AccountExistsError,
  ContactClaimRequiredError,
  DuplicateContactError,
} from '@/shared/domain'
import type { UpdateMyProfile, UpdateMyProfileParams } from '@/modules/auth/use-cases'

type ApiUser = {
  id: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  phone?: string
  isStaff?: boolean
  onboardingStep?: number
  measurementSystem?: MeasurementSystem
}

type ApiResponse = {
  user: ApiUser
}

export class EdgeUpdateMyProfileRepository {
  constructor(private readonly httpClient: HttpPatch) {}

  execute: UpdateMyProfile = async (params) => {
    try {
      const body = buildBody(params)
      const response = await this.httpClient.patch<typeof body, ApiResponse>({
        url: '/users/me',
        body,
      })
      return mapUser(response.user)
    } catch (error) {
      if (error instanceof HttpError && error.status === 409) {
        const conflict = readContactConflict(error.body)
        if (conflict?.reason === 'contact_claim_required') {
          throw new ContactClaimRequiredError(
            conflict.identifier,
            conflict.identifierType,
            conflict.pendingInvite,
            conflict.holderUnregistered,
          )
        }
        if (conflict?.reason === 'account_exists') {
          throw new AccountExistsError(conflict.identifier, conflict.identifierType)
        }
        throw new DuplicateContactError()
      }
      throw error
    }
  }
}

function readContactConflict(body: unknown): {
  reason: 'contact_claim_required' | 'account_exists'
  identifier: string
  identifierType: 'email' | 'phone'
  pendingInvite: boolean
  holderUnregistered: boolean
} | null {
  if (typeof body !== 'object' || body === null) return null
  const data = (body as { data?: unknown }).data
  if (typeof data !== 'object' || data === null) return null
  const { reason, identifier, identifierType, pendingInvite, holderUnregistered } = data as {
    reason?: unknown
    identifier?: unknown
    identifierType?: unknown
    pendingInvite?: unknown
    holderUnregistered?: unknown
  }
  if (reason !== 'contact_claim_required' && reason !== 'account_exists') return null
  if (typeof identifier !== 'string') return null
  if (identifierType !== 'email' && identifierType !== 'phone') return null
  return {
    reason,
    identifier,
    identifierType,
    pendingInvite: pendingInvite === true,
    holderUnregistered: holderUnregistered === true,
  }
}

function buildBody(params: UpdateMyProfileParams) {
  const body: UpdateMyProfileParams = {}
  if (params.firstName !== undefined) body.firstName = params.firstName
  if (params.lastName !== undefined) body.lastName = params.lastName
  if (params.displayName !== undefined) body.displayName = params.displayName
  if (params.phone !== undefined) body.phone = params.phone
  if (params.email !== undefined) body.email = params.email
  if (params.flywayRegion !== undefined) body.flywayRegion = params.flywayRegion
  if (params.onboardingStep !== undefined) body.onboardingStep = params.onboardingStep
  if (params.measurementSystem !== undefined) body.measurementSystem = params.measurementSystem
  return body
}

function mapUser(user: ApiUser): UserProfile {
  return {
    userId: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    displayName: user.displayName,
    phone: user.phone,
    isStaff: user.isStaff,
    onboardingStep: user.onboardingStep,
    measurementSystem: user.measurementSystem,
  }
}
